import React, { useEffect, useState } from "react";
import { withPrefix } from "gatsby";
import { Link, useI18next } from "gatsby-plugin-react-i18next";
import { useTranslation, Trans } from "react-i18next";

//react-bootstrap
import { Container, Row, Col, Stack, Dropdown } from "react-bootstrap";
import ArrowToggle from "../icons/ArrowToggle";
import ExternalLink from "./external-link";
import InternalLink from "./internal-link";
import { gtag } from "../../hooks/use-gtag-navigate";

const links: {
  name: string;
  links: {
    name: string;
    to: string;
    external?: boolean;
    blank?: boolean;
    logo?: string;
  }[];
}[] = [
  {
    name: "features",
    links: [
      {
        name: "online_store",
        to: "/features/online-store",
      },
      {
        name: "pos",
        to: "/features/cloud-pos",
      },
      {
        name: "payment",
        to: "/features/payment-and-delivery-logistics",
      },
      {
        name: "inventory",
        to: "/features/inventory-management",
      },
      {
        name: "customer",
        to: "/features/crm-and-loyalty",
      },
      {
        name: "social",
        to: "/features/social-commerce",
      },
      {
        name: "chatbot",
        to: "/features/ai-chatbot",
      },
      {
        name: "online_booking",
        to: "/features/online-booking-system",
      },
    ],
  },
  {
    name: "storeberry",
    links: [
      {
        name: "price",
        to: "/price",
      },
      {
        name: "blog",
        to: "/blog",
      },
      {
        name: "setup",
        to: "/store-setup",
      },
      {
        name: "guide",
        to: "https://www.storeberry.ai/hk/user-guide/common-setting",
        external: true,
        blank: true,
      },
      {
        name: "login",
        to: "https://www.mystoreberry.com/auth/login?click=footer-menu",
        external: true,
        blank: true,
      },
    ],
  },
  {
    name: "about_storeberry",
    links: [
      {
        name: "about",
        to: "/about-us",
      },
      {
        name: "contact",
        to: "/contact-us",
      },
      {
        name: "facebook",
        to: "https://www.facebook.com/storeberry.retail/",
        external: true,
        blank: true,
      },
      {
        name: "instagram",
        to: "https://www.instagram.com/storeberry.ai/",
        external: true,
        blank: true,
      },
    ],
  },
  {
    name: "storeberry_app",
    links: [
      {
        name: "iphone",
        to: "https://apps.apple.com/hk/app/storeberry/id1369841488?l=en",
        external: true,
        blank: true,
        logo: "images/favicon/icon_app_ios.svg",
      },
      {
        name: "ipad",
        to: "https://apps.apple.com/hk/app/storeberry-for-ipad/id1421691113",
        external: true,
        blank: true,
        logo: "images/favicon/icon_app_ios.svg",
      },
    ],
  },
];

const countries: { name: string; to: string }[] = [
  {
    name: "global",
    to: "https://www.storeberry.ai/",
  },
  {
    name: "united_kingdom",
    to: "https://www.storeberry.ai/uk",
  },
  {
    name: "hong_kong",
    to: "https://www.storeberry.ai//hk",
  },
];
const Footer = () => {
  const { language, languages, changeLanguage, path } = useI18next();
  const { t } = useTranslation();

  return (
    <footer id="footer" className="footer">
      <Container fluid="md">
        <Row className="py-3">
          <Col xs={12} lg={3} className="pb-5">
            <div className="footer-logo d-block pb-5">
              <img
                className="img-fluid"
                src={withPrefix("images/logo-full-en.svg")}
                loading="lazy"
                alt="storeberry"
              />
            </div>
            <p className="footer_description">{t("footer.slogan")}</p>
            <div className="d-flex">
              <ExternalLink
                onClick={() => {
                  gtag();
                }}
                href="https://www.facebook.com/storeberry.retail/"
                className="pr-4"
              >
                <i className="h2 lab la-facebook"></i>
              </ExternalLink>
              <ExternalLink
                onClick={() => {
                  gtag();
                }}
                href="https://www.instagram.com/storeberry.ai/"
                className="pr-4"
              >
                <i className="h2 lab la-instagram"></i>
              </ExternalLink>
            </div>
          </Col>
          {links.map((item) => (
            <Col key={item.name} xs={6} lg={2}>
              <h4 className="pb-5 footer-title">{t(`footer.${item.name}`)}</h4>
              <Stack>
                {item.links.map((link) =>
                  link.external ? (
                    link.logo ? (
                      <div key={link.to} className="py-3">
                        {t(`menu.${link.name}`)}
                        <ExternalLink
                          onClick={() => {
                            gtag();
                          }}
                          key={link.to}
                          href={link.to}
                          className="icon-link"
                          target={link.blank ? "_blank" : "_self"}
                        >
                          {link.logo ? (
                            <img src={withPrefix(link.logo)} alt={link.name} loading="lazy" />
                          ) : (
                            <div />
                          )}
                        </ExternalLink>
                      </div>
                    ) : (
                      <ExternalLink
                        onClick={() => {
                          gtag();
                        }}
                        key={link.to}
                        href={link.to}
                        target={link.blank ? "_blank" : "_self"}
                        className="nav-link py-3"
                      >
                        <div> {t(`menu.${link.name}`)}</div>
                      </ExternalLink>
                    )
                  ) : (
                    <InternalLink
                      onClick={() => {
                        gtag();
                      }}
                      key={link.to}
                      href={link.to}
                      target={link.blank ? "_blank" : "_self"}
                      className="nav-link py-3"
                    >
                      <div> {t(`menu.${link.name}`)}</div>
                    </InternalLink>
                  )
                )}
              </Stack>
            </Col>
          ))}
        </Row>
        <hr className="m-0" />
        <Row className="footer-height justify-content-lg-between text-lg-center">
          <Col md={4} className="">
            <p className="small text-left d-flex justify-content-start">
              ©{new Date().getFullYear()} {t("footer.copyright")}
            </p>
          </Col>
          <Col md={8} className="">
            <div className="d-flex small justify-content-md-end">
              <InternalLink onClick={()=>{gtag()}}  className="nav-link" href="/terms-and-conditions">
                <div>{t("footer.termsConditions")}</div>
              </InternalLink>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              <InternalLink onClick={()=>{gtag()}} className="nav-link" href="/privacy-policy">
                <div>{t("footer.policy")}</div>
              </InternalLink>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              <Dropdown>
                <Dropdown.Toggle as={ArrowToggle}>
                  <i className="las la-globe"></i>
                  {t("country.current")}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {countries.map((item) => (
                    <Dropdown.Item key={item.name} href={item.to}>
                      {t(`country.${item.name}`)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              <Dropdown>
                <Dropdown.Toggle as={ArrowToggle}>
                  <i className="las la-language"></i>
                  {t(language)}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {languages.map((lang) => (
                    <Dropdown.Item
                      key={lang}
                      onClick={(e) => {
                        e.preventDefault;
                        changeLanguage(lang);
                      }}
                    >
                      {t(lang)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Col>
        </Row>
      </Container>
    </footer>
  );
};

Footer.propTypes = {};

export default Footer;
